import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { usePDF, Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';
import Button from 'react-bootstrap/esm/Button';
import { formatCurrency } from '../../helpers/formatting';
import { ESTIMATE_REQUIRED } from '../../modules/Screen/reducer';

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'row',
    paddingTop: 35,
    paddingBottom: 60,
    paddingHorizontal: 15,
  },
  section: {
    margin: 10,
    padding: 10,
    flexGrow: 1,
  },
  title: {
    fontSize: 24,
    textAlign: 'center',
  },
  header: {
    fontSize: 12,
    marginBottom: 20,
    textAlign: 'center',
    color: 'grey',
  },
  text: {
    margin: 12,
    fontSize: 14,
    textAlign: 'justify',
    fontFamily: 'Times-Roman',
  },
  date: {
    position: 'absolute',
    fontSize: 12,
    top: 15,
    right: 15,
    color: 'grey',
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  stage: {
    fontSize: 14,
    textDecoration: 'underline',
    paddingVertical: 5,
  },
  subStage: {
    fontSize: 13,
    paddingVertical: 5,
    paddingLeft: 10,
  },
  category: {
    fontSize: 12,
    paddingVertical: 5,
    paddingLeft: 20,
    maxWidth: 400,
  },
  fee: {
    fontSize: 12,
    paddingVertical: 5,
  },
  sum: {
    fontSize: 14,
    textAlign: 'right',
    paddingVertical: 5,
  },
  disclaimer: {
    position: 'absolute',
    fontSize: 10,
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: 'grey',
    paddingHorizontal: 30,
  },
});

const propTypes = {
  activeCategories: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      feeType: PropTypes.string,
      feeValue: PropTypes.number.isRequired,
      feeQuantity: PropTypes.number,
    }),
  ),
  activeStages: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      id: PropTypes.number,
      sort: PropTypes.number,
      subStages: PropTypes.array,
    }),
  ),
  scheduleName: PropTypes.string,
  scheduleGroup: PropTypes.string,
  calculation: PropTypes.number,
};

export default function CalculationPDF(props: InferProps<typeof propTypes>): JSX.Element {
  const { activeCategories, activeStages, scheduleName, scheduleGroup, calculation } = props;
  let date = new Date().toLocaleDateString('en-NZ');
  const MyDoc = (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <Text style={styles.title}>Criminal Legal Aid Fixed Fees</Text>
          <Text style={styles.header}>{scheduleGroup} Prosecution, {scheduleName}</Text>
          {activeStages?.map((stage, index) => {
            return (
              <View key={stage.name}>
                <Text style={styles.stage}>{stage.name}</Text>
                {stage.subStages?.map((subStage, index) => {
                  return (
                    <View key={subStage.name}>
                      <Text style={styles.subStage}>{subStage.name}</Text>
                      {subStage.categories?.map((category, index) => {
                        let active = false;
                        if (activeCategories?.includes(category)) {
                          active = true;
                        }
                        return (
                          <View key={category.name} style={styles.row}>
                            <Text style={styles.category}>{`${category.name}: `}
                              {active && category.feeQuantity ? `($${formatCurrency(category.feeValue, false,)} x ${category.feeQuantity})` : null}
                            </Text>
                            <Text style={styles.fee}>
                              {active
                                ? category.feeType === ESTIMATE_REQUIRED
                                  ? 'Estimate required'
                                  : (category.feeQuantity
                                    ? `$${formatCurrency(category.feeValue * category.feeQuantity)}`
                                    : `$${formatCurrency(category.feeValue)}`
                                  )
                                : '$0.00'}
                            </Text>
                          </View>
                        );
                      })}
                    </View>
                  );
                })}
              </View>
            );
          })}
          <Text style={styles.sum}>Total excluding GST: ${formatCurrency(calculation)}</Text>
          <Text style={styles.date}>{date}</Text>
        </View>
        <Text style={styles.disclaimer} fixed>
          This summary is not an invoice and will not be accepted by Legal Aid Grants as an invoice.  You will need to fill in an invoice using the appropriate form.
        </Text>
      </Page>
    </Document>
  );

  const [instance, updateInstance] = usePDF({ document: MyDoc });

  if (instance.loading) return <div>Loading ...</div>;

  if (instance.error) return <div>Something went wrong: {error}</div>;

  return (
    <Button variant="success" className="themed-btn" href={instance.url} download="legal-aid-fees.pdf">
      Export to PDF
    </Button>
  );
}
